import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Home,
  X,
  FileText,
} from "lucide-react";
import { FloorPlan } from "../types";
import { useFloorPlanInteraction } from "../lib/animations/useFloorPlanInteraction";
import {
  MeasurementTools,
  MeasurementPointIndicator,
  MeasurementLineIndicator,
} from "./MeasurementTools";
import { PDFViewer } from "./PDFViewer";
interface InteractiveFloorPlanViewerProps {
  floorPlans: FloorPlan[];
  className?: string;
}

export const InteractiveFloorPlanViewer: React.FC<
  InteractiveFloorPlanViewerProps
> = ({ floorPlans, className = "" }) => {
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [viewMode, setViewMode] = useState<"image" | "concept" | "brochure">(
    "concept"
  );

  const {
    measurementMode,
    measurementPoints,
    measurementLines,
    x,
    y,
    scale,
    containerRef,
    imageRef,
    zoomIn,
    zoomOut,
    resetView,
    toggleMeasurementMode,
    clearMeasurements,
    handleImageClick,
    handleKeyDown,
    formatDistance,
  } = useFloorPlanInteraction({
    floorPlanScale: 0.1,
  });

  const currentPlan = floorPlans[selectedPlan];
  const currentImage =
    isFlipped && currentPlan.flippedImage
      ? currentPlan.flippedImage
      : currentPlan.image;

  // Keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className={`bg-white rounded-2xl shadow-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Home className="w-6 h-6 text-blue-600" />
            Interactive Floor Plans
          </h3>

          <div className="flex items-center gap-2">
            {viewMode === "image" && (
              <MeasurementTools
                isActive={measurementMode}
                points={measurementPoints}
                lines={measurementLines}
                onToggle={toggleMeasurementMode}
                onClear={clearMeasurements}
                formatDistance={formatDistance}
              />
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowLightbox(true)}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Maximize2 className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Plan Selector */}
        {floorPlans.length > 1 && (
          <div className="flex space-x-2 mb-4">
            {floorPlans.map((plan, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedPlan(index)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  selectedPlan === index
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {plan.label}
              </motion.button>
            ))}
          </div>
        )}

        {/* View Mode Selector */}
        <div className="flex space-x-2 mb-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setViewMode("concept")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
              viewMode === "concept"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm">Concept Plan</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setViewMode("brochure")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
              viewMode === "brochure"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm">Client Brochure</span>
          </motion.button>
        </div>

        {/* Controls - Only show for image mode */}
        {viewMode === "image" && (
          <div className="flex flex-wrap gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={zoomIn}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ZoomIn className="w-4 h-4" />
              <span className="text-sm">Zoom In</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={zoomOut}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ZoomOut className="w-4 h-4" />
              <span className="text-sm">Zoom Out</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetView}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm">Reset</span>
            </motion.button>

            {currentPlan.flippedImage && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsFlipped(!isFlipped)}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="text-sm">Flip</span>
              </motion.button>
            )}
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className="relative">
        {viewMode === "concept" && currentPlan.conceptPlanUrl && (
          <PDFViewer
            file={currentPlan.conceptPlanUrl}
            title={`${currentPlan.label} - Concept Plan`}
            className=""
          />
        )}

        {viewMode === "brochure" && currentPlan.clientBrochureUrl && (
          <PDFViewer
            file={currentPlan.clientBrochureUrl}
            title={`${currentPlan.label} - Client Brochure`}
            className=""
          />
        )}

        {viewMode === "image" && (
          <div
            className="relative overflow-hidden bg-gray-50"
            style={{ height: "500px" }}
          >
            <motion.div
              ref={containerRef}
              drag
              dragConstraints={containerRef}
              style={{ x, y, scale }}
              className="relative w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
            >
              {/* Floor Plan Image */}
              <img
                ref={imageRef}
                src={currentImage}
                alt={currentPlan.label}
                className="max-w-full max-h-full object-contain select-none"
                onClick={handleImageClick}
                draggable={false}
              />

              {/* Hotspots */}

              {/* Measurement Points and Lines */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Measurement Points */}
                {measurementPoints.map((point, index) => (
                  <MeasurementPointIndicator
                    key={point.id}
                    point={point}
                    index={index}
                    isActive={measurementMode}
                  />
                ))}

                {/* Measurement Lines */}
                {measurementLines.length > 0 && (
                  <svg className="absolute inset-0 w-full h-full">
                    {measurementLines.map((line, index) => (
                      <MeasurementLineIndicator
                        key={line.id}
                        line={line}
                        formatDistance={formatDistance}
                        index={index}
                      />
                    ))}
                  </svg>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowLightbox(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black/50 p-2 rounded-full"
              >
                <X className="w-6 h-6" />
              </motion.button>
              <img
                src={currentImage}
                alt={currentPlan.label}
                className="max-w-full max-h-full object-contain"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
